import type {
  BuiltInDataTableSettings,
  KeyAttributeSettings,
  KeyAttributeSettingOptions,
  KeyMeasureSettings,
  KeyMeasureSettingOptions,
} from "building-types/dataSequences/scheduleSettingsTable";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import { ensureDefined } from "common/utils/assertOfType";
import {
  formatDataSequenceAttributeDisplayName,
  BUILTIN_DATA_SEQUENCE_ENTITY_TYPES_TO_OMIT_MEASURES,
} from "doc/utils/dataSequenceTools";
import { BUILTIN_DATASEQUENCE_CATALOG } from "./dataSequenceCatalog";
import { fillBuiltInDataSequenceKAMOptionDefaults } from "./dataSequences";
import { DATA_TABLE_ATTRIBUTE_OVERRIDES } from "./dataTableSettings";

export function getDefaultBuiltInDataTableSettings(
  type: SystemDataSequenceEntityType,
  scheduleName: string,
): BuiltInDataTableSettings {
  const catalogSelector = BUILTIN_DATASEQUENCE_CATALOG[type];

  if (catalogSelector.dataSequenceType === "TABLE_SEQUENCE") {
    return {
      type,
      scheduleName,
      attributes: {},
      measures: {},
      footnote: "",
      forceColumnNameUppercase: true,
      isActive: true,
    };
  }

  const baseOptions = fillBuiltInDataSequenceKAMOptionDefaults(catalogSelector, true);
  const settingsAttributes: KeyAttributeSettings = {};
  let index = 0;

  for (const attribute in baseOptions.attributes) {
    const baseAttribute = ensureDefined(baseOptions.attributes[attribute]);
    settingsAttributes[attribute] = {
      ...baseAttribute,
      unit: baseAttribute.unit,
      index: index++,
      isHidden: !baseAttribute.definesEntity,
      width: undefined,
      displayName: formatDataSequenceAttributeDisplayName(type, attribute),
      includeFilter: "",
      excludeFilter: "",
    } as KeyAttributeSettingOptions;
  }

  if (BUILTIN_DATA_SEQUENCE_ENTITY_TYPES_TO_OMIT_MEASURES[type]) {
    return {
      type,
      scheduleName,
      attributes: settingsAttributes,
      measures: {},
      footnote: "",
      forceColumnNameUppercase: true,
      isActive: true,
    };
  }

  const settingsMeasures: KeyMeasureSettings = {};

  for (const measure in baseOptions.measures) {
    const baseMeasure = ensureDefined(baseOptions.measures[measure]);
    settingsMeasures[measure] = {
      ...baseMeasure,
      unit: baseMeasure.unit,
      index: index++,
      isHidden: false,
      width: undefined,
      displayName: `Total ${formatDataSequenceAttributeDisplayName(type, measure)}`,
    } as KeyMeasureSettingOptions;
  }

  return {
    type,
    scheduleName,
    attributes: settingsAttributes,
    measures: settingsMeasures,
    footnote: "",
    forceColumnNameUppercase: true,
    isActive: true,
  };
}

export function getBuiltInDataTableSettings(
  type: SystemDataSequenceEntityType,
  scheduleName: string,
): BuiltInDataTableSettings {
  const defaultSettings = getDefaultBuiltInDataTableSettings(type, scheduleName);

  for (const id in defaultSettings.attributes) {
    if (defaultSettings.attributes[id] && DATA_TABLE_ATTRIBUTE_OVERRIDES[type]?.[id]) {
      defaultSettings.attributes[id] = {
        ...defaultSettings.attributes[id],
        ...DATA_TABLE_ATTRIBUTE_OVERRIDES[type][id],
      };
    }
  }

  return defaultSettings;
}
